import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RefreshCw,
  Settings,
  ListTree,
  Loader2,
  FileUp,
  FileSpreadsheet,
  ExternalLink,
} from 'lucide-react';
import { LoginHistoryList, Collapsible, type AuditSession } from '@cca/ui';
import { api } from '../api/client';
import type { ParsedXlsx, ParsedPL, ParsedChurchIncome } from '../lib/xeroPLParser';
import { parseXeroXlsx } from '../lib/xeroPLParser';

type XeroAccountsStatus = { count: number; lastSyncedAt: string };
type XeroPLStatus = {
  count: number;
  lastImportedAt: string;
  periods: string[];
  sourceFile: string;
};

type SyncSummary = {
  accountsCached: number;
  income: number;
  expenses: number;
  gstProfilesUpdated?: number;
};
type XlsxApplySummary = {
  amountsMatched: number;
  amountsZeroed: number;
  periodUsed: string;
};

type ReapplySectionsSummary = {
  sectionsApplied: number;
  rowsReassigned: number;
};

type Props = {
  onClose: () => void;
  /** Called after a successful P&L import so the budget table can refresh its actuals column. */
  onActualsChange?: () => void;
  /** Codes currently in the budget — used to flag P&L rows with no budget match
   *  and budget rows missing from the P&L. */
  budgetCodes?: string[];
  /** Reconcile budget rows against Xero: refresh GL cache, add new codes,
   *  rename to Xero canonical, hard-delete codes that have left Xero. */
  onSyncBudgetFromXero?: () => Promise<SyncSummary | null>;
  /** Apply Tab 2 (Statement of Financial Performance) to the budget:
   *  section restructure + currentAmount mapping in one go, with auto-save
   *  so a refresh doesn't lose the result. */
  onApplyXlsxToBudget?: () => Promise<XlsxApplySummary | null>;
  /** Escape-hatch action: overwrites tool-managed sections with the
   *  structure from the most recent xlsx Tab 2 import. */
  onReapplySectionsFromXlsx?: () => Promise<ReapplySectionsSummary | null>;
};

const EMPTY_ACCOUNTS_STATUS: XeroAccountsStatus = { count: 0, lastSyncedAt: '' };
const EMPTY_PL_STATUS: XeroPLStatus = { count: 0, lastImportedAt: '', periods: [], sourceFile: '' };

const formatSyncedAt = (iso: string): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-NZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: false,
  });
};

export function AdminPanel({
  onClose,
  onActualsChange,
  budgetCodes,
  onSyncBudgetFromXero,
  onApplyXlsxToBudget,
  onReapplySectionsFromXlsx,
}: Props) {
  const [syncSummary, setSyncSummary] = useState<SyncSummary | null>(null);
  const [xlsxApplySummary, setXlsxApplySummary] = useState<XlsxApplySummary | null>(null);
  const [reapplySectionsSummary, setReapplySectionsSummary] =
    useState<ReapplySectionsSummary | null>(null);
  const [sheetUrl, setSheetUrl] = useState<string>('');
  // Xero connection is owned by the hub admin (one shared OAuth across
  // all hub tools) — the budget tool no longer manages it. We keep a
  // stub for the legacy `status.connected` / `status.secretSet` checks
  // sprinkled through this component so we don't have to surgically
  // remove every gate; both are always true here.
  const status = { connected: true, secretSet: true };
  const [accountsStatus, setAccountsStatus] = useState<XeroAccountsStatus>(EMPTY_ACCOUNTS_STATUS);
  const [plStatus, setPlStatus] = useState<XeroPLStatus>(EMPTY_PL_STATUS);
  const [plCodes, setPlCodes] = useState<string[]>([]);
  const [lastSample, setLastSample] = useState<string[] | null>(null);
  const [busy, setBusy] = useState<
    'idle' | 'loading' | 'syncing' | 'parsing' | 'importing' | 'gst-update'
  >('loading');
  const [error, setError] = useState<string | null>(null);
  const [gstUpdateResult, setGstUpdateResult] = useState<{
    updated: number;
    skipped: number;
  } | null>(null);

  const [pendingPL, setPendingPL] = useState<{ parsed: ParsedXlsx; fileName: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Registrar-only login + activity audit (403 for non-registrar admins).
  const [sessions, setSessions] = useState<AuditSession[] | null>(null);
  const [sessionsError, setSessionsError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    api
      .loginHistory()
      .then((r) => alive && setSessions(r.sessions))
      .catch((e) => alive && setSessionsError((e as Error).message || 'Could not load sign-ins.'));
    return () => {
      alive = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setBusy('loading');
    setError(null);
    try {
      const [a, p, actuals, url] = await Promise.all([
        api.budgetGLStatus(),
        api.budgetPLStatus(),
        api.budgetPLActuals(),
        Promise.resolve(''),
      ]);
      setAccountsStatus(a);
      setPlStatus(p);
      setPlCodes(Object.keys(actuals.byCode));
      setSheetUrl(url);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('idle');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleReapplySections = async () => {
    if (!onReapplySectionsFromXlsx) return;
    const ok = window.confirm(
      'Overwrite the tool-managed sections with the structure from your last xlsx Tab 2 upload?\n\n' +
        'This replaces every section name, ordering, and per-row assignment with what Tab 2 says. ' +
        'Use this after materially restructuring your Xero report.',
    );
    if (!ok) return;
    setReapplySectionsSummary(null);
    setError(null);
    try {
      const result = await onReapplySectionsFromXlsx();
      if (result) setReapplySectionsSummary(result);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const handleSyncAccounts = async () => {
    if (!onSyncBudgetFromXero) return;
    setBusy('syncing');
    setError(null);
    setSyncSummary(null);
    setLastSample(null);
    try {
      const summary = await onSyncBudgetFromXero();
      if (summary) {
        setSyncSummary(summary);
        setAccountsStatus({
          count: summary.accountsCached,
          lastSyncedAt: new Date().toISOString(),
        });
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('idle');
    }
  };

  const handleUpdateGst = async () => {
    setBusy('gst-update');
    setError(null);
    setGstUpdateResult(null);
    try {
      const result = await api.budgetApplyGst();
      setGstUpdateResult(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('idle');
    }
  };

  const handlePLFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;
    setBusy('parsing');
    setError(null);
    setPendingPL(null);
    try {
      const parsed = await parseXeroXlsx(file);
      if (!parsed.pAndL && !parsed.performance && !parsed.churchIncome) {
        throw new Error(
          'No recognised tabs in the workbook. Expecting at least one of: ' +
            '"Profit and Loss", "Statement of Financial Performance", or "Church Income".',
        );
      }
      setPendingPL({ parsed, fileName: file.name });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy('idle');
    }
  };

  const handlePLConfirm = async () => {
    if (!pendingPL) return;
    setBusy('importing');
    setError(null);
    setXlsxApplySummary(null);
    try {
      const result = await api.budgetXlsxImport({
        pAndL: pendingPL.parsed.pAndL,
        performance: pendingPL.parsed.performance,
        churchIncome: pendingPL.parsed.churchIncome,
        sourceFile: pendingPL.fileName,
      });
      if (result.pAndL) {
        setPlStatus({
          count: result.pAndL.count,
          lastImportedAt: result.pAndL.lastImportedAt,
          periods: result.pAndL.periods,
          sourceFile: result.pAndL.sourceFile,
        });
        setPlCodes(pendingPL.parsed.pAndL?.rows.map((r) => r.code) ?? []);
      }
      setPendingPL(null);
      onActualsChange?.();
      // Apply Tab 2 to the budget (sections + amounts) and auto-save —
      // single round-trip so a page refresh keeps the result.
      if (pendingPL.parsed.performance && onApplyXlsxToBudget) {
        const applied = await onApplyXlsxToBudget();
        if (applied) setXlsxApplySummary(applied);
      }
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy('idle');
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- backdrop click-to-close duplicates the explicit Close button inside
    <div className="fixed inset-0 z-40 bg-black/30 no-print" onClick={onClose}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events -- click handler only stops backdrop-close propagation, no action of its own */}
      <div
        className="absolute right-0 top-0 bottom-0 w-full max-w-[480px] bg-paper-cream shadow-xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-paper-cream border-b border-paper-divider px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Settings className="w-5 h-5 text-ccaanz-gold" />
            <h2 className="text-[16px] font-medium text-ccaanz-black">Admin</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-ccaanz-grey hover:text-ccaanz-black text-[14px]"
            aria-label="Close"
          >
            Close
          </button>
        </div>

        <div className="p-6 space-y-6">
          <section className="panel">
            <div className="panel-section">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[14px] font-medium text-ccaanz-black">Xero connection</h3>
                <button
                  type="button"
                  onClick={refresh}
                  className="text-ccaanz-grey hover:text-ccaanz-black"
                  title="Refresh status"
                  disabled={busy === 'loading'}
                >
                  <RefreshCw
                    className={`w-3.5 h-3.5 ${busy === 'loading' ? 'animate-spin' : ''}`}
                  />
                </button>
              </div>

              {error && <div className="mb-3 text-[12px] text-ccaanz-red">{error}</div>}

              <p className="text-[12px] text-ccaanz-grey leading-relaxed">
                Xero is connected through the hub. Manage the connection from the hub dashboard
                banner — the same OAuth covers Reimbursement, Inbox, and this tool.
              </p>

              {sheetUrl && (
                <div className="mt-4">
                  <a
                    href={sheetUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-ghost btn-sm"
                    title="Open the data Sheet in a new tab"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Open data Sheet
                  </a>
                </div>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-section">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[14px] font-medium text-ccaanz-black flex items-center gap-2">
                  <ListTree className="w-4 h-4 text-ccaanz-gold" />
                  Chart of Accounts
                </h3>
              </div>

              <div className="text-[12px] text-ccaanz-grey leading-relaxed mb-4">
                Wipes the budget structure and replaces every line with Xero's active revenue +
                expense accounts (one row per GL code, set to 0). Run this whenever the Chart of
                Accounts changes — then upload the xlsx to fill in the values and section grouping.
              </div>

              <div className="text-[13px] mb-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-ccaanz-grey">Cached accounts</span>
                  <span className="num text-ccaanz-black font-medium">{accountsStatus.count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ccaanz-grey">Last synced</span>
                  <span className="num text-ccaanz-black">
                    {formatSyncedAt(accountsStatus.lastSyncedAt)}
                  </span>
                </div>
              </div>

              <button
                type="button"
                onClick={handleSyncAccounts}
                disabled={busy !== 'idle' || !status.connected}
                className="btn btn-blue btn-sm"
                title={status.connected ? 'Pull GL codes from Xero' : 'Connect to Xero first'}
              >
                {busy === 'syncing' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                {busy === 'syncing' ? 'Syncing…' : 'Sync from Xero'}
              </button>

              {lastSample && lastSample.length > 0 && (
                <div className="mt-4 pt-3 border-t border-paper-divider">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-gold font-medium mb-2">
                    Sample
                  </div>
                  <ul className="text-[12px] text-ccaanz-black space-y-0.5">
                    {lastSample.map((s) => (
                      <li key={s} className="num">
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {syncSummary && (
                <div className="mt-4 pt-3 border-t border-paper-divider text-[12px] space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Accounts cached from Xero</span>
                    <span className="num text-ccaanz-black">{syncSummary.accountsCached}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Income rows in budget</span>
                    <span className="num text-ccaanz-black">{syncSummary.income}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Expense rows in budget</span>
                    <span className="num text-ccaanz-black">{syncSummary.expenses}</span>
                  </div>
                  {syncSummary.gstProfilesUpdated !== undefined && (
                    <div className="flex items-center justify-between">
                      <span className="text-ccaanz-grey">Cashflow GST flags updated from Xero</span>
                      <span className="num text-ccaanz-black">
                        {syncSummary.gstProfilesUpdated}
                      </span>
                    </div>
                  )}
                  <div className="text-ccaanz-grey pt-1">
                    All previous budget structure replaced with Xero's revenue + expense accounts.
                    Upload the xlsx next to populate amounts and sections. Save to persist.
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-section">
              <div className="flex items-baseline justify-between mb-3">
                <h3 className="text-[14px] font-medium text-ccaanz-black flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-ccaanz-gold" />
                  Cashflow GST flags
                </h3>
              </div>

              <div className="text-[12px] text-ccaanz-grey leading-relaxed mb-4">
                Reads the TaxType field from the synced Xero accounts and updates the
                <em> gstApplicable</em> flag on each cashflow timing profile. Safe — does not change
                budget structure, amounts, or sections. Run after syncing accounts or whenever Xero
                TaxType assignments change.
              </div>

              <button
                type="button"
                onClick={handleUpdateGst}
                disabled={busy !== 'idle' || !status.connected}
                className="btn btn-blue btn-sm"
                title={
                  status.connected ? 'Update GST flags from Xero TaxType' : 'Connect to Xero first'
                }
              >
                {busy === 'gst-update' ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="w-3.5 h-3.5" />
                )}
                {busy === 'gst-update' ? 'Updating…' : 'Update GST from Xero'}
              </button>

              {gstUpdateResult && (
                <div className="mt-4 pt-3 border-t border-paper-divider text-[12px] space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Timing profiles updated</span>
                    <span className="num text-ccaanz-black font-medium">
                      {gstUpdateResult.updated}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Profiles skipped (no Xero match)</span>
                    <span className="num text-ccaanz-black">{gstUpdateResult.skipped}</span>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-section">
              <h3 className="text-[14px] font-medium text-ccaanz-black flex items-center gap-2 mb-3">
                <FileSpreadsheet className="w-4 h-4 text-ccaanz-gold" />
                Profit &amp; Loss actuals
              </h3>

              <div className="text-[12px] text-ccaanz-grey leading-relaxed mb-4">
                Upload a single workbook with three tabs:
                <ul className="list-disc ml-5 mt-1 space-y-0.5">
                  <li>
                    <strong className="not-italic font-medium">Profit and Loss</strong> — FY actuals
                    (used for the FY actual column)
                  </li>
                  <li>
                    <strong className="not-italic font-medium">
                      Statement of Financial Performance
                    </strong>{' '}
                    — projected current year + section grouping/order
                  </li>
                  <li>
                    <strong className="not-italic font-medium">Church Income</strong> — stored for a
                    future feature
                  </li>
                </ul>
              </div>

              <div className="text-[13px] mb-4 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-ccaanz-grey">Cached rows</span>
                  <span className="num text-ccaanz-black font-medium">{plStatus.count}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ccaanz-grey">Periods</span>
                  <span className="num text-ccaanz-black">
                    {plStatus.periods.length > 0 ? plStatus.periods.join(', ') : '—'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-ccaanz-grey">Last imported</span>
                  <span className="num text-ccaanz-black">
                    {formatSyncedAt(plStatus.lastImportedAt)}
                  </span>
                </div>
                {plStatus.sourceFile && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-ccaanz-grey flex-shrink-0">Source file</span>
                    <span
                      className="text-ccaanz-black text-[11px] truncate"
                      title={plStatus.sourceFile}
                    >
                      {plStatus.sourceFile}
                    </span>
                  </div>
                )}
              </div>

              {xlsxApplySummary && (
                <div className="mt-4 pt-3 border-t border-paper-divider text-[12px] space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">
                      Rows populated (Proj FY{xlsxApplySummary.periodUsed})
                    </span>
                    <span className="num text-ccaanz-black font-medium">
                      {xlsxApplySummary.amountsMatched}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-ccaanz-grey">Rows zeroed (no match in Tab 2)</span>
                    <span className="num text-ccaanz-black">{xlsxApplySummary.amountsZeroed}</span>
                  </div>
                  <div className="text-ccaanz-grey pt-1">
                    Sections untouched — tool-managed via Sections page. Auto-saved.
                  </div>
                </div>
              )}

              {plCodes.length > 0 && budgetCodes && budgetCodes.length > 0 && (
                <CodeMatchDiagnostic plCodes={plCodes} budgetCodes={budgetCodes} />
              )}

              {plStatus.count > 0 && onReapplySectionsFromXlsx && (
                <div className="mt-4 pt-3 border-t border-paper-divider">
                  <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-gold font-medium mb-2">
                    Advanced
                  </div>
                  <div className="text-[12px] text-ccaanz-grey leading-relaxed mb-3">
                    Sections are tool-managed via the{' '}
                    <strong className="font-medium not-italic">Sections</strong> page. Use this only
                    if you've materially restructured your Xero report and want the tool to mirror
                    it — every section name, ordering, and per-row assignment will be replaced by
                    Tab 2's structure.
                  </div>
                  <button
                    type="button"
                    onClick={handleReapplySections}
                    className="btn btn-ghost btn-sm"
                  >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Reapply sections from Tab 2
                  </button>
                  {reapplySectionsSummary && (
                    <div className="mt-3 text-[12px] space-y-1">
                      <div className="flex items-center justify-between">
                        <span className="text-ccaanz-grey">Sections applied</span>
                        <span className="num text-ccaanz-black">
                          {reapplySectionsSummary.sectionsApplied}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-ccaanz-grey">Rows reassigned</span>
                        <span className="num text-ccaanz-black font-medium">
                          {reapplySectionsSummary.rowsReassigned}
                        </span>
                      </div>
                      <div className="text-ccaanz-grey pt-1">Auto-saved.</div>
                    </div>
                  )}
                </div>
              )}

              {!pendingPL && (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={handlePLFile}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy !== 'idle'}
                    className="btn btn-blue btn-sm"
                  >
                    {busy === 'parsing' ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <FileUp className="w-3.5 h-3.5" />
                    )}
                    {busy === 'parsing' ? 'Parsing…' : 'Upload P&L'}
                  </button>
                </>
              )}

              {pendingPL && (
                <div className="mt-2 border border-edge rounded-control p-3 bg-white">
                  <div className="text-[12px] text-ccaanz-grey mb-2">Preview</div>
                  <div className="text-[13px] mb-3">
                    <div className="font-medium text-ccaanz-black truncate">
                      {pendingPL.fileName}
                    </div>
                  </div>
                  <div className="text-[12px] space-y-2 mb-3">
                    <TabPreview
                      label="Profit and Loss"
                      hint="→ FY{prior} actuals column"
                      tab={pendingPL.parsed.pAndL}
                    />
                    <TabPreview
                      label="Statement of Financial Performance"
                      hint="→ Proj FY{base} amounts + section structure"
                      tab={pendingPL.parsed.performance}
                    />
                    <TabPreview
                      label="Church Income"
                      hint="→ stored for later use"
                      raw={pendingPL.parsed.churchIncome}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handlePLConfirm}
                      disabled={busy !== 'idle'}
                      className="btn btn-blue btn-sm"
                    >
                      {busy === 'importing' ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <FileUp className="w-3.5 h-3.5" />
                      )}
                      {busy === 'importing' ? 'Importing…' : 'Confirm import'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingPL(null)}
                      disabled={busy !== 'idle'}
                      className="btn btn-ghost btn-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>
          </section>

          <section className="panel">
            <div className="panel-section">
              <Collapsible
                title={
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4 text-ccaanz-gold" />
                    Login history
                  </span>
                }
                hint={sessions?.length}
              >
                <p className="text-[12px] text-ccaanz-grey leading-relaxed mb-3">
                  Your portal sign-ins — when each started, how long it stayed active, how it ended,
                  and what you worked on. "Active" updates while a tab is open. Newest first (last
                  100).
                </p>
                {sessionsError && (
                  <div className="mb-3 text-[12px] text-ccaanz-red">{sessionsError}</div>
                )}
                {sessions === null ? (
                  <div className="text-[12px] text-ccaanz-grey">Loading…</div>
                ) : (
                  <LoginHistoryList sessions={sessions} accent="#85754d" />
                )}
              </Collapsible>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function TabPreview({
  label,
  hint,
  tab,
  raw,
}: {
  label: string;
  hint: string;
  tab?: ParsedPL | null;
  raw?: ParsedChurchIncome | null;
}) {
  const present = !!(tab || raw);
  return (
    <div className="border border-paper-divider rounded-control px-3 py-2">
      <div className="flex items-center justify-between gap-2">
        <span className={`font-medium ${present ? 'text-ccaanz-black' : 'text-ccaanz-grey'}`}>
          {present ? '●' : '○'} {label}
        </span>
        <span className="text-[10px] text-ccaanz-grey">{hint}</span>
      </div>
      {tab && (
        <div className="mt-1 text-[11px] text-ccaanz-grey space-x-3">
          <span>{tab.rows.length} accounts</span>
          <span>periods: {tab.periods.join(', ')}</span>
        </div>
      )}
      {raw && (
        <div className="mt-1 text-[11px] text-ccaanz-grey">
          {raw.churches.length} churches · {raw.years.length} years
        </div>
      )}
      {!present && (
        <div className="mt-1 text-[11px] text-ccaanz-grey">Not in workbook — skipped</div>
      )}
    </div>
  );
}

function CodeMatchDiagnostic({
  plCodes,
  budgetCodes,
}: {
  plCodes: string[];
  budgetCodes: string[];
}) {
  const plSet = new Set(plCodes);
  const budgetSet = new Set(budgetCodes.filter((c) => c));

  const matched = budgetCodes.filter((c) => c && plSet.has(c));
  const budgetOrphans = [...budgetSet].filter((c) => !plSet.has(c)).sort();
  const plOrphans = plCodes.filter((c) => !budgetSet.has(c)).sort();

  return (
    <div className="mt-4 pt-3 border-t border-paper-divider">
      <div className="text-[10px] uppercase tracking-[0.08em] text-ccaanz-gold font-medium mb-2">
        Match diagnostic
      </div>
      <div className="text-[12px] space-y-1 mb-3">
        <div className="flex items-center justify-between">
          <span className="text-ccaanz-grey">Budget rows matched to P&amp;L</span>
          <span className="num text-ccaanz-black font-medium">
            {matched.length} / {budgetCodes.length}
          </span>
        </div>
      </div>
      {budgetOrphans.length > 0 && (
        <details className="mb-2">
          <summary className="text-[12px] text-ccaanz-grey cursor-pointer hover:text-ccaanz-black">
            {budgetOrphans.length} budget code{budgetOrphans.length === 1 ? '' : 's'} not in P&amp;L
            <span className="text-[10px] ml-2">(rows that show "—")</span>
          </summary>
          <div className="mt-1 text-[11px] text-ccaanz-grey num">{budgetOrphans.join(', ')}</div>
        </details>
      )}
      {plOrphans.length > 0 && (
        <details>
          <summary className="text-[12px] text-ccaanz-grey cursor-pointer hover:text-ccaanz-black">
            {plOrphans.length} P&amp;L code{plOrphans.length === 1 ? '' : 's'} with no budget row
          </summary>
          <div className="mt-1 text-[11px] text-ccaanz-grey num">{plOrphans.join(', ')}</div>
        </details>
      )}
    </div>
  );
}

// Removed: StatusRow (status now hub-managed; banner lives on hub dashboard).
// Removed: formatTimeLeft + ViewerLinkPanel (no token-based viewer links — every visitor is allowlisted).
